import { createContext, useContext, useEffect } from "react";
import { useThemePreference } from "../components/base/AppsSidebar/hooks/useThemePreference";
import { applyDarkClass } from "../theme/applyTheme";

export type ThemeMode = "light" | "dark" | "system";

export interface ThemeContextType {
  isDarkMode: boolean;
  themeMode: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { themeMode, isDarkMode, setTheme } = useThemePreference();

  useEffect(() => {
    applyDarkClass(isDarkMode);
  }, [isDarkMode]);

  const toggleTheme = () => {
    if (themeMode === "system") {
      setTheme(isDarkMode ? "light" : "dark");
    } else {
      setTheme(themeMode === "dark" ? "light" : "dark");
    }
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, themeMode, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

/**
 * @deprecated Use useTheme() instead. This alias is provided for backward compatibility.
 */
export function useDarkMode(): ThemeContextType {
  return useTheme();
}
